import { ComponentType, PropsWithChildren } from 'react';
import { Redirect, matchPath } from 'react-router-dom';

import { generateDynamicPath } from 'common/utils/routing';
import { AppRoutes } from 'modules/router';
import { NattersRoutes } from 'natters/routes';
import { NatalieRoutes } from 'pages/Natalie/routes';

import { useAuth } from './AuthProvider';

const PROTECTED_TO_PUBLIC_ROUTES = [
  { path: NattersRoutes.NatterDetails, publicRoute: NattersRoutes.NatterPublic },
  { path: NatalieRoutes.Details, publicRoute: NatalieRoutes.Public },
  { path: AppRoutes.EventDetails, publicRoute: AppRoutes.EventPublic },
];

/**
 * Returns the public page path for known protected entity routes, carrying the current query
 * across. Returns null if the path doesn't match any known pattern.
 *
 * The query is appended verbatim rather than passed through `generateDynamicPath`'s `queryParams`,
 * which takes a record and so would round-trip through `URLSearchParams` — re-encoding values and
 * collapsing repeated keys. This matches how the sign-in branch below carries `window.location`
 * forward, and keeps signals the public page reads (`?testInviteCode=`) and ones only analytics
 * reads (`?utm_*`) intact for a visitor who arrives signed out.
 */
const getPublicRedirectPath = (pathname: string): string | null => {
  for (const { path, publicRoute } of PROTECTED_TO_PUBLIC_ROUTES) {
    const match = matchPath<{ id: string }>(pathname, { path, exact: true });
    if (match) return generateDynamicPath(publicRoute, { id: match.params.id }) + window.location.search;
  }

  return null;
};

export const ProtectedRoute = ({ children }: PropsWithChildren) => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    const publicPath = getPublicRedirectPath(window.location.pathname);
    if (publicPath) {
      return <Redirect to={publicPath} />;
    }

    return (
      <Redirect
        to={{ pathname: AppRoutes.Login, state: { redirectUri: window.location.pathname + window.location.search } }}
      />
    );
  }

  return <>{children}</>;
};

export const withProtectedRoute = (Component: ComponentType) => () => (
  <ProtectedRoute>
    <Component />
  </ProtectedRoute>
);
